/**
 * Linha do tempo de quem falou, por gravação (spec 016, fase 2).
 *
 * A gravação vira um Blob que passeia: listener da ficha, sessão, IndexedDB,
 * e volta como Blob NOVO quando a ficha recarrega. Em vez de mexer no
 * formato da sessão, a linha do tempo fica guardada à parte, pela
 * "impressão digital" do Blob (tipo + tamanho em bytes — duas gravações
 * com o mesmo tamanho exato não acontecem na prática). Memória primeiro;
 * localStorage pra sobreviver ao recarregar. Só horários e "médico/sala":
 * nada clínico sai daqui.
 */

import { textoLinhaDoTempo, type TrechoFala } from './falante';

const LS_KEY = 'dratento.voice.falantes.v1';
/** Gravações guardadas (as mais velhas saem). */
const MAX = 40;

export interface FalantesGravacao {
  /** Segundos gravados (sem as pausas). */
  duracao: number;
  trechos: TrechoFala[];
}

const porBlob = new WeakMap<Blob, FalantesGravacao>();

const chave = (b: Blob) => `${b.type}|${b.size}`;

function lerLs(): Array<[string, FalantesGravacao]> {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? (v as Array<[string, FalantesGravacao]>) : [];
  } catch {
    return [];
  }
}

export function guardarFalantes(blob: Blob, reg: FalantesGravacao): void {
  porBlob.set(blob, reg);
  try {
    const k = chave(blob);
    const lista = lerLs().filter(([c]) => c !== k);
    lista.push([k, reg]);
    localStorage.setItem(LS_KEY, JSON.stringify(lista.slice(-MAX)));
  } catch {
    /* storage cheio/bloqueado: vale só nesta aba */
  }
}

export function falantesDe(blob: Blob): FalantesGravacao | null {
  const mem = porBlob.get(blob);
  if (mem) return mem;
  const achado = lerLs().find(([c]) => c === chave(blob));
  if (!achado) return null;
  const reg = achado[1];
  if (typeof reg?.duracao !== 'number' || !Array.isArray(reg.trechos)) return null;
  porBlob.set(blob, reg);
  return reg;
}

/**
 * Texto pra IA dos áudios juntados em sequência (como a ficha manda: um
 * Blob atrás do outro). Cada gravação desloca as seguintes pela sua
 * duração. Para na primeira sem medida — dali em diante não dá pra saber
 * o horário. Null = nada medido.
 */
export function linhaDoTempoDosAudios(blobs: readonly Blob[]): string | null {
  const todos: TrechoFala[] = [];
  let desloca = 0;
  let medidoAte: number | undefined;
  for (let i = 0; i < blobs.length; i++) {
    const reg = falantesDe(blobs[i]);
    if (!reg) {
      medidoAte = desloca;
      break;
    }
    for (const t of reg.trechos) todos.push({ de: t.de + desloca, ate: t.ate + desloca, quem: t.quem });
    desloca += reg.duracao;
  }
  if (todos.length === 0) return null;
  return textoLinhaDoTempo(todos, medidoAte);
}
